import { buildUniqueSelector } from './selector-utils';

export class KeyboardFocusableElementsRule {
	constructor(i18n) {
		this.__ = i18n;
		this.key = 'keyboardFocusable';
	}

	getDefaultStatus() {
		return 'not_checked';
	}

	evaluate(documentNode, pageUrl = '') {
		const candidates = this.getCandidateNodes(documentNode);
		const elements = candidates.map((node, index) => this.buildElementInfo(node, index, pageUrl));
		const focusableElements = elements.filter((element) => element.isKeyboardFocusable);
		const elementIssues = elements.filter((element) => element.issue);
		const dialogChecks = this.getDialogNodes(documentNode).map((node, index) => this.buildDialogCheck(node, index, pageUrl));
		const dialogIssues = dialogChecks.filter((check) => check.issue);
		const issues = [...elementIssues, ...dialogIssues];

		if (elements.length === 0) {
			return {
				status: 'pass',
				message: this.__( 'No keyboard-focusable candidates found.', 'a11yfix' ),
				details: {
					elements,
					focusableElements,
					dialogChecks,
					dialogIssues,
					issues
				}
			};
		}

		if (issues.length > 0) {
			return {
				status: 'fail',
				message: `${this.__( 'Keyboard focus issues found:', 'a11yfix' )} ${issues.length}`,
				details: {
					elements,
					focusableElements,
					dialogChecks,
					dialogIssues,
					issues
				}
			};
		}

		return {
			status: 'pass',
			message: this.__( 'Keyboard focusable elements look good.', 'a11yfix' ),
			details: {
				elements,
				focusableElements,
				dialogChecks,
				dialogIssues,
				issues
			}
		};
	}

	getCandidateNodes(documentNode) {
		const selector = [
			'a[href]',
			'area[href]',
			'button',
			'input:not([type="hidden"])',
			'select',
			'textarea',
			'summary',
			'iframe',
			'audio[controls]',
			'video[controls]',
			'[contenteditable]:not([contenteditable="false"])',
			'[tabindex]',
			'[role="button"]',
			'[role="link"]',
			'[role="checkbox"]',
			'[role="radio"]',
			'[role="switch"]',
			'[role="tab"]',
			'[role="menuitem"]',
			'[role="textbox"]'
		].join(', ');

		return Array.from(documentNode.querySelectorAll(selector)).filter((node) => {
			if (node.closest('#wpadminbar')) {
				return false;
			}

			if (!this.isVisible(node)) {
				return false;
			}

			return true;
		});
	}

	buildElementInfo(node, index, pageUrl) {
		const tag = node.tagName.toLowerCase();
		const type = tag === 'input' ? (node.getAttribute('type') || 'text').toLowerCase() : tag;
		const id = node.getAttribute('id') || '';
		const name = node.getAttribute('name') || '';
		const role = node.getAttribute('role') || '';
		const rawTabindex = node.getAttribute('tabindex');
		const tabindex = rawTabindex === null ? null : Number.parseInt(rawTabindex, 10);
		const hasInvalidTabindex = rawTabindex !== null && Number.isNaN(tabindex);
		const isDisabled = this.isDisabled(node);
		const isNaturallyFocusable = this.isNaturallyFocusable(node);
		const isKeyboardFocusable = this.isKeyboardFocusable(node, isNaturallyFocusable, isDisabled, tabindex, hasInvalidTabindex);
		const hasVisibleFocusIndicator = this.hasVisibleFocusIndicator(node);
		const hasSuppressedFocusStyle = this.hasSuppressedFocusStyle(node);
		const hasVisualOrderOverride = this.hasVisualOrderOverride(node);
		const needsExplicitKeyboardSupport = this.needsExplicitKeyboardSupport(node, isNaturallyFocusable);
		const hasExplicitKeyboardHandler = this.hasExplicitKeyboardHandler(node);
		let issue = '';

		if (hasInvalidTabindex) {
			issue = this.__( 'Invalid tabindex value.', 'a11yfix' );
		} else if (tabindex !== null && tabindex < 0 && this.requiresKeyboardAccess(node)) {
			issue = this.__( 'Interactive element removed from tab order (tabindex < 0).', 'a11yfix' );
		} else if (!isKeyboardFocusable && this.requiresKeyboardAccess(node)) {
			issue = this.__( 'Interactive element is not keyboard-focusable.', 'a11yfix' );
		} else if (tabindex !== null && tabindex > 0) {
			issue = this.__( 'Avoid positive tabindex values; use 0 or native order.', 'a11yfix' );
		} else if (hasVisualOrderOverride && isKeyboardFocusable) {
			issue = this.__( 'Element uses CSS order which can break logical keyboard navigation order.', 'a11yfix' );
		} else if (needsExplicitKeyboardSupport && !hasExplicitKeyboardHandler) {
			issue = this.__( 'Custom interactive role may be missing Enter/Space keyboard handling.', 'a11yfix' );
		} else if (hasSuppressedFocusStyle && this.requiresKeyboardAccess(node)) {
			issue = this.__( 'Focus styles appear to be removed without a visible replacement.', 'a11yfix' );
		}

		return {
			index: index + 1,
			url: pageUrl,
			selector: buildUniqueSelector(node),
			tag,
			type,
			id,
			name,
			role,
			tabindex: rawTabindex === null ? '' : rawTabindex,
			isNaturallyFocusable,
			isKeyboardFocusable,
			isDisabled,
			hasVisibleFocusIndicator,
			hasSuppressedFocusStyle,
			hasVisualOrderOverride,
			needsExplicitKeyboardSupport,
			hasExplicitKeyboardHandler,
			issue
		};
	}

	getDialogNodes(documentNode) {
		const selector = ['dialog[open]', '[role="dialog"]', '[role="alertdialog"]'].join(', ');

		return Array.from(documentNode.querySelectorAll(selector)).filter((node) => {
			if (node.closest('#wpadminbar')) {
				return false;
			}

			return this.isVisible(node);
		});
	}

	buildDialogCheck(node, index, pageUrl) {
		const role = (node.getAttribute('role') || '').toLowerCase();
		const tag = node.tagName.toLowerCase();
		const ariaModal = (node.getAttribute('aria-modal') || '').toLowerCase();
		const focusableDescendants = this.getFocusableDescendants(node);
		const hasAutofocusTarget = focusableDescendants.some((item) => item.hasAttribute('autofocus'));
		const isContainerFocusable = this.isDialogContainerFocusable(node);
		let issue = '';

		if (focusableDescendants.length === 0) {
			issue = this.__( 'Dialog has no focusable elements to support keyboard use.', 'a11yfix' );
		} else if ((role === 'dialog' || role === 'alertdialog') && ariaModal !== 'true') {
			issue = this.__( 'Dialog should set aria-modal="true" when it behaves as a modal with focus trap.', 'a11yfix' );
		} else if (!hasAutofocusTarget && !isContainerFocusable) {
			issue = this.__( 'Dialog may not receive initial focus when opened; add autofocus target or tabindex to container.', 'a11yfix' );
		}

		return {
			index: index + 1,
			url: pageUrl,
			selector: buildUniqueSelector(node),
			tag,
			id: node.getAttribute('id') || '',
			name: '',
			role,
			ariaModal,
			focusableCount: focusableDescendants.length,
			hasAutofocusTarget,
			isContainerFocusable,
			issue
		};
	}

	getFocusableDescendants(containerNode) {
		const selector = [
			'a[href]',
			'area[href]',
			'button',
			'input:not([type="hidden"])',
			'select',
			'textarea',
			'summary',
			'iframe',
			'audio[controls]',
			'video[controls]',
			'[contenteditable]:not([contenteditable="false"])',
			'[tabindex]:not([tabindex="-1"])'
		].join(', ');

		return Array.from(containerNode.querySelectorAll(selector)).filter((node) => this.isVisible(node) && !this.isDisabled(node));
	}

	isDialogContainerFocusable(node) {
		const rawTabindex = node.getAttribute('tabindex');

		if (rawTabindex === null) {
			return false;
		}

		const parsed = Number.parseInt(rawTabindex, 10);
		return !Number.isNaN(parsed) && parsed >= -1;
	}

	isVisible(node) {
		const defaultView = node.ownerDocument.defaultView || window;
		let current = node;

		while (current && current.nodeType === 1) {
			const style = defaultView.getComputedStyle(current);

			if (style.display === 'none' || style.visibility === 'hidden') {
				return false;
			}

			if (Number.parseFloat(style.opacity || '1') === 0) {
				return false;
			}

			current = current.parentElement;
		}

		return true;
	}

	isDisabled(node) {
		if ('disabled' in node && node.disabled) {
			return true;
		}

		const ariaDisabled = (node.getAttribute('aria-disabled') || '').toLowerCase();

		if (ariaDisabled === 'true') {
			return true;
		}

		if (node.closest('fieldset[disabled]')) {
			return true;
		}

		return false;
	}

	isNaturallyFocusable(node) {
		const tag = node.tagName.toLowerCase();

		if (tag === 'a' || tag === 'area') {
			return node.hasAttribute('href');
		}

		if (['button', 'select', 'textarea', 'iframe', 'summary'].includes(tag)) {
			return true;
		}

		if (tag === 'input') {
			const type = (node.getAttribute('type') || 'text').toLowerCase();
			return type !== 'hidden';
		}

		if ((tag === 'audio' || tag === 'video') && node.hasAttribute('controls')) {
			return true;
		}

		if (node.isContentEditable) {
			return true;
		}

		return false;
	}

	isKeyboardFocusable(node, isNaturallyFocusable, isDisabled, tabindex, hasInvalidTabindex) {
		if (isDisabled || hasInvalidTabindex) {
			return false;
		}

		if (tabindex !== null) {
			return tabindex >= 0;
		}

		if (isNaturallyFocusable) {
			return true;
		}

		if (node.isContentEditable) {
			return true;
		}

		return false;
	}

	hasVisibleFocusIndicator(node) {
		const defaultView = node.ownerDocument.defaultView || window;
		const baseStyle = defaultView.getComputedStyle(node);
		const focusStyle = defaultView.getComputedStyle(node, ':focus');
		const focusVisibleStyle = defaultView.getComputedStyle(node, ':focus-visible');
		const stylesToCheck = [focusVisibleStyle, focusStyle, baseStyle];

		for (const style of stylesToCheck) {
			const outlineStyle = String(style.outlineStyle || '').toLowerCase();
			const outlineWidth = Number.parseFloat(style.outlineWidth || '0');
			const boxShadow = String(style.boxShadow || '').toLowerCase();

			if (outlineStyle !== 'none' && outlineWidth > 0) {
				return true;
			}

			if (boxShadow && boxShadow !== 'none') {
				return true;
			}
		}

		return false;
	}

	hasSuppressedFocusStyle(node) {
		const inlineStyle = String(node.getAttribute('style') || '').toLowerCase();
		const inlineSuppressesOutline = inlineStyle.includes('outline:none') || inlineStyle.includes('outline: none');
		const inlineSuppressesShadow = inlineStyle.includes('box-shadow:none') || inlineStyle.includes('box-shadow: none');

		if (inlineSuppressesOutline && (inlineSuppressesShadow || !this.hasVisibleFocusIndicator(node))) {
			return true;
		}

		const classList = Array.from(node.classList || []);
		const id = String(node.getAttribute('id') || '').trim();
		const tag = String(node.tagName || '').toLowerCase();
		const styleNodes = Array.from(node.ownerDocument.querySelectorAll('style'));

		for (const styleNode of styleNodes) {
			const cssText = String(styleNode.textContent || '');
			if (!cssText) {
				continue;
			}

			for (const className of classList) {
				const classPattern = new RegExp(`\\.${className}(?:[^\\{]*):(?:focus|focus-visible)[^\\{]*\\{[^\\}]*outline\\s*:\\s*none`, 'i');
				if (classPattern.test(cssText)) {
					return true;
				}
			}

			if (id) {
				const idPattern = new RegExp(`#${id}(?:[^\\{]*):(?:focus|focus-visible)[^\\{]*\\{[^\\}]*outline\\s*:\\s*none`, 'i');
				if (idPattern.test(cssText)) {
					return true;
				}
			}

			const tagPattern = new RegExp(`${tag}(?:[^\\{]*):(?:focus|focus-visible)[^\\{]*\\{[^\\}]*outline\\s*:\\s*none`, 'i');
			if (tagPattern.test(cssText)) {
				return true;
			}
		}

		return false;
	}

	hasVisualOrderOverride(node) {
		const defaultView = node.ownerDocument.defaultView || window;
		const style = defaultView.getComputedStyle(node);
		const orderValue = Number.parseInt(style.order || '0', 10);
		const parentStyle = node.parentElement ? defaultView.getComputedStyle(node.parentElement) : null;
		const parentDisplay = String(parentStyle?.display || '').toLowerCase();
		const inFlexOrGrid = parentDisplay.includes('flex') || parentDisplay.includes('grid');

		if (Number.isNaN(orderValue)) {
			return false;
		}

		return inFlexOrGrid && orderValue !== 0;
	}

	needsExplicitKeyboardSupport(node, isNaturallyFocusable) {
		if (isNaturallyFocusable) {
			return false;
		}

		const role = (node.getAttribute('role') || '').toLowerCase();
		const interactiveRoles = ['button', 'link', 'checkbox', 'radio', 'switch', 'tab', 'menuitem', 'textbox'];
		const hasTabindex = node.getAttribute('tabindex') !== null;

		return hasTabindex && interactiveRoles.includes(role);
	}

	hasExplicitKeyboardHandler(node) {
		const keyboardAttrs = ['onkeydown', 'onkeyup', 'onkeypress'];

		return keyboardAttrs.some((attrName) => {
			const value = node.getAttribute(attrName);
			return typeof value === 'string' && value.trim().length > 0;
		});
	}

	requiresKeyboardAccess(node) {
		if (this.isNaturallyFocusable(node)) {
			return true;
		}

		const role = (node.getAttribute('role') || '').toLowerCase();
		const interactiveRoles = ['button', 'link', 'checkbox', 'radio', 'switch', 'tab', 'menuitem', 'textbox'];

		return interactiveRoles.includes(role);
	}
}